import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import type {
  InsightsLabV2Favorite,
  InsightsLabV2Report,
} from 'api/insightsLabV2'
import Pagination from 'components/Pagination'
import SearchInput from 'components/SearchInput'
import SideNav from 'components/SideNav'
import usePaginateArray from 'hooks/usePaginateArray'
import { buildTranslate } from 'locales'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Col, Row } from 'react-bootstrap-five'
import CampaignBridgeBanner from './CampaignBridgeBanner'
import FavoriteRow from './FavoriteRow'
import ReportButton from './ReportButton'

const t = buildTranslate('insights_lab_v2.picker')

// Matches v1's Lab.tsx page size for the same report-list pagination.
const REPORTS_PER_PAGE = 15

// Matched on the name because no category id reaches this payload: the Looker
// sync persists the category folder's name only, and a report's own
// looker_folder_id is its dashboard folder, not its category's.
const CAMPAIGN_BRIDGE_CATEGORY = 'Campaigns & rewards'

type Props = {
  reports: InsightsLabV2Report[]
  favorites: InsightsLabV2Favorite[]
  onOpenReport: (report: InsightsLabV2Report) => void
  onOpenFavorite: (favorite: InsightsLabV2Favorite) => void
  onDeleteFavorite: (id: number) => void
  // Height of the scrollable browser body. The picker modal uses the default
  // fixed height; the inline (no-report-open) usage fills more of the page.
  bodyHeight?: string
  // Which sidebar section to land on initially, sourced from the nav's
  // "+N more" overflow links (see Content.tsx's `section` query param) so
  // "+N more" under Recommended doesn't silently land on Favorites. Only
  // honored when that section actually has content for the current
  // merchant/data — otherwise falls through to the "All reports" default
  // below. Undefined preserves that default entirely, which keeps
  // ReportPickerModal (not reachable from those nav links) behaving exactly
  // as before.
  initialSection?: 'recommended' | 'favorites'
}

// The report browser: a category sidebar (All reports, then Favorites,
// first) + search over the catalog, rendering report cards or saved
// favorites. Used both inside the "See all reports" modal and inline as the
// Insights Lab empty state.
const ReportBrowser = ({
  reports,
  favorites,
  onOpenReport,
  onOpenFavorite,
  onDeleteFavorite,
  bodyHeight = '500px',
  initialSection,
}: Props): JSX.Element => {
  const [css] = useStyletron()

  // Reuses the existing picker modal title translation so "All reports"
  // isn't duplicated as a separate string.
  const allReportsLabel = t('title')
  const favoritesLabel = t('favorites')
  const recommendedLabel = t('recommended')

  const categories = useMemo(
    () =>
      Array.from(
        new Set(
          reports.map(report => report.categoryName).filter(Boolean) as string[]
        )
      )
        // The All reports/Favorites/Recommended labels are reserved section
        // keys; drop any real category with the same name so it can't
        // collide in the sidebar.
        .filter(
          name =>
            name !== allReportsLabel &&
            name !== favoritesLabel &&
            name !== recommendedLabel
        )
        .sort((a, b) => a.localeCompare(b)),
    [reports, allReportsLabel, favoritesLabel, recommendedLabel]
  )

  // The globally-recommended reports, ordered — surfaced as a pseudo-category
  // right after Favorites.
  const recommendedReports = useMemo(
    () =>
      reports
        .filter(report => report.recommendedPosition != null)
        .sort(
          (a, b) => (a.recommendedPosition ?? 0) - (b.recommendedPosition ?? 0)
        ),
    [reports]
  )

  // Default to "All reports" — it's never empty once there's at least one
  // report or favorite anywhere, so the landing view is never blank.
  // `initialSection` (from the nav's overflow links) takes priority over
  // that default, but only when it actually has content — an
  // `initialSection` requesting an empty section falls through to "All
  // reports" rather than landing on an empty list.
  const [selected, setSelected] = useState<string>(() => {
    if (initialSection === 'recommended' && recommendedReports.length > 0) {
      return recommendedLabel
    }
    if (initialSection === 'favorites' && favorites.length > 0) {
      return favoritesLabel
    }
    return allReportsLabel
  })
  const [search, setSearch] = useState('')
  // The favorite whose removal is awaiting confirmation (the star reveals an
  // inline "Remove this favorite?" prompt rather than deleting on first click).
  const [confirmingId, setConfirmingId] = useState<number | null>(null)

  // Re-navigates when initialSection changes to a NEW value (e.g. the user
  // clicks a different section's "+N more" nav link while this browser is
  // already mounted with no report tabs open) -- the useState initializer
  // above only ever runs once, so without this, a changed `?section=` in the
  // URL had no visible effect. Guarded against the ref so this doesn't
  // re-fire (and fight manual category browsing) on every unrelated
  // favorites/recommendedReports update while initialSection itself is
  // unchanged.
  const appliedInitialSectionRef = useRef(initialSection)
  useEffect(() => {
    if (initialSection === appliedInitialSectionRef.current) return
    appliedInitialSectionRef.current = initialSection
    if (initialSection === 'recommended' && recommendedReports.length > 0) {
      setSelected(recommendedLabel)
    } else if (initialSection === 'favorites' && favorites.length > 0) {
      setSelected(favoritesLabel)
    }
  }, [
    initialSection,
    recommendedReports.length,
    favorites.length,
    recommendedLabel,
    favoritesLabel,
  ])

  useEffect(() => {
    // "All reports" never needs a fallback — there's nothing "after" it, and
    // it always has content as long as there's at least one report or
    // favorite total.
    const selectedHasContent =
      selected === allReportsLabel ||
      (selected === favoritesLabel && favorites.length > 0) ||
      (selected === recommendedLabel && recommendedReports.length > 0) ||
      categories.includes(selected)
    if (selectedHasContent) return
    if (favorites.length > 0) {
      setSelected(favoritesLabel)
    } else if (recommendedReports.length > 0) {
      setSelected(recommendedLabel)
    } else {
      setSelected(categories[0] ?? favoritesLabel)
    }
  }, [
    allReportsLabel,
    categories,
    favorites.length,
    favoritesLabel,
    recommendedLabel,
    recommendedReports.length,
    selected,
  ])

  const navItems = [
    allReportsLabel,
    favoritesLabel,
    ...(recommendedReports.length > 0 ? [recommendedLabel] : []),
    ...categories,
  ]
  const query = search.trim().toLowerCase()
  const isSearching = query !== ''
  const showFavorites = !isSearching && selected === favoritesLabel
  const showRecommended = !isSearching && selected === recommendedLabel
  const showAllReports = !isSearching && selected === allReportsLabel
  // Category view only -- search results span categories, so this pointer
  // wouldn't describe them. Case-insensitive: the name comes from a
  // human-edited Looker folder.
  const showCampaignBridge =
    !isSearching &&
    selected.toLowerCase() === CAMPAIGN_BRIDGE_CATEGORY.toLowerCase()
  // Favorites matching the search term — surfaced above the report results so
  // a saved favorite is findable by search, not just by browsing to the
  // Favorites section. Matches on the favorite's own (possibly custom) name
  // or the underlying report's title. Favorites already arrive from the API
  // most-recently-created first — this only filters, it never reorders.
  const favoriteMatches = useMemo(
    () =>
      isSearching
        ? favorites.filter(
            favorite =>
              favorite.name.toLowerCase().includes(query) ||
              (favorite.reportTitle ?? '').toLowerCase().includes(query)
          )
        : [],
    [isSearching, favorites, query]
  )

  // Reports matching the search term, excluding ones already surfaced via
  // favoriteMatches -- a favorited report whose title matches the query
  // would otherwise appear twice (once opening the favorite, once opening
  // the report itself). Only excludes for an unfiltered favorite (no saved
  // filterState): a filtered favorite opens a genuinely different state
  // than the plain report, so it isn't a true duplicate and both should
  // stay listed.
  const searchedReports = useMemo(() => {
    const unfilteredFavoriteReportIds = new Set(
      favoriteMatches
        .filter(favorite => Object.keys(favorite.filterState).length === 0)
        .map(favorite => favorite.lookerReportId)
    )
    return reports.filter(
      report =>
        !unfilteredFavoriteReportIds.has(report.id) &&
        (report.title.toLowerCase().includes(query) ||
          (report.categoryName ?? '').toLowerCase().includes(query))
    )
  }, [reports, query, favoriteMatches])

  // Reports for the current non-search sidebar selection. "All reports" is a
  // pseudo-category — no real report's categoryName ever equals its label —
  // so it's special-cased to the full list; any real category filters to
  // just itself.
  const selectedCategoryReports = useMemo(
    () =>
      selected === allReportsLabel
        ? reports
        : reports.filter(report => report.categoryName === selected),
    [reports, selected, allReportsLabel]
  )

  const visibleReports = isSearching ? searchedReports : selectedCategoryReports

  // Right-pane list when not showing the Favorites-only block (All reports,
  // Recommended, a category, or search results).
  const listReports = showRecommended ? recommendedReports : visibleReports
  const listEmptyMessage = showRecommended
    ? t('no_recommended')
    : t('no_reports')

  // Paginates `listReports` only (15/page, matching v1's Lab.tsx). Favorites
  // (the plain Favorites tab and `secondaryFavorites` below) are
  // intentionally never paginated -- they're a small, curated set, not a
  // browsable catalog, so showing them in full is a deliberate scope
  // limitation rather than an oversight.
  const {
    currentPage,
    setPage,
    paginatedData: paginatedListReports,
    totalPages,
  } = usePaginateArray(listReports, REPORTS_PER_PAGE)

  // The favorites shown alongside `listReports` in that shared right-pane
  // block — search matches while searching, every favorite for "All
  // reports" (its content is the union of everything), or none for a plain
  // category/Recommended (those only ever show `listReports`). Computed
  // once and reused for both rendering and the empty-state check below, per
  // this file's no-nested-ternary convention.
  const getSecondaryFavorites = (): InsightsLabV2Favorite[] => {
    if (isSearching) return favoriteMatches
    if (showAllReports) return favorites
    return []
  }
  const secondaryFavorites = getSecondaryFavorites()
  // Computed against the full, unpaginated `listReports` -- pagination only
  // affects what's rendered on the current page, not whether there are any
  // results at all.
  const noResults = secondaryFavorites.length === 0 && listReports.length === 0

  const renderFavoriteRow = (favorite: InsightsLabV2Favorite): JSX.Element => (
    <FavoriteRow
      key={favorite.id}
      favorite={favorite}
      isConfirming={confirmingId === favorite.id}
      onOpenFavorite={onOpenFavorite}
      onRequestRemove={() => setConfirmingId(favorite.id)}
      onConfirmRemove={() => {
        onDeleteFavorite(favorite.id)
        setConfirmingId(null)
      }}
      onCancelRemove={() => setConfirmingId(null)}
    />
  )

  return (
    <>
      <SearchInput
        className="w-100 mb-l"
        value={search}
        onChange={value => {
          setSearch(value)
          setPage(1)
        }}
        placeholder={t('search_placeholder')}
      />
      <Row className={css({ height: bodyHeight, margin: 0 })}>
        {!isSearching && (
          <Col
            md={3}
            className={css({
              height: '100%',
              overflowY: 'auto',
              paddingLeft: 0,
            })}
          >
            <SideNav
              items={navItems}
              current={selected}
              onClick={section => {
                setSelected(section)
                setPage(1)
              }}
              noBorderRadius
            />
          </Col>
        )}
        <Col className={css({ height: '100%', overflowY: 'auto' })}>
          {/* Mirrors the v1 category heading (Lab.tsx's `Text.Header3` above
              the report list). Hidden while searching -- search results are
              combined across categories/favorites, so a single category name
              wouldn't describe them. */}
          {!isSearching && (
            <Text.Header3 className="mt-0 mb-s">{selected}</Text.Header3>
          )}
          {showFavorites ? (
            <>
              {favorites.length === 0 && (
                <Text.BodyText3 color="grey70">
                  {t('no_favorites')}
                </Text.BodyText3>
              )}
              {favorites.map(renderFavoriteRow)}
            </>
          ) : (
            <>
              {showCampaignBridge && <CampaignBridgeBanner />}
              {secondaryFavorites.map(renderFavoriteRow)}
              {noResults && (
                <Text.BodyText3 color="grey70">
                  {listEmptyMessage}
                </Text.BodyText3>
              )}
              {paginatedListReports.map(report => (
                <ReportButton
                  key={report.id}
                  report={report}
                  onOpen={onOpenReport}
                />
              ))}
              {totalPages > 1 && (
                <div className="d-flex justify-content-center margin-top-huge">
                  <Pagination
                    currentPage={currentPage}
                    numPages={totalPages}
                    onChangePage={setPage}
                  />
                </div>
              )}
            </>
          )}
        </Col>
      </Row>
    </>
  )
}

export default ReportBrowser
